/**
 * billing-tab.tsx — Tab "Gói & thanh toán": gói hiện tại + số đã mua + kỳ hiện hành,
 * lịch sử đơn (mọi trạng thái) + hộp chi tiết đơn (báo giá lưu lúc đặt, hoá đơn điện tử),
 * thao tác quản trị có lý do: đổi gói (theo bảng giá), gia hạn thủ công, đặt hạn cấp phép.
 */
import { useState } from 'react'
import { CalendarClock, CalendarPlus, FileSignature, Repeat } from 'lucide-react'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Pagination } from '@/components/shared/pagination'
import { ErrorState, Loading } from '@/components/shared/feedback'
import { formatVnd } from '@/lib/format-money'
import { formatNumber } from '@/lib/utils'
import { platformApiError } from '@/lib/platform-client'
import {
  useChangeOrgPlan, useExtendOrg, useOrgBilling, useOrgOrder, useOrgOrders, useSaveOrgContract, useSetOrgExpiry,
  type CatalogPlan, type ContractLimits, type OrgOrderRow, type Quotas,
} from '@/hooks/use-platform-org-manage'
import {
  CONTRACT_FIELDS, QUOTA_FIELDS, contractFromForm, contractToForm, quantityAdjust, type FormValues,
} from './contract-form'
import { comparePlans, vatBreakdown, vatLabel } from '@/pages/billing/plans'
import { OrgStatusBadge, toDateInput } from '../org-status'
import { ReasonDialog } from './reason-dialog'
import { apiErrorCode, endOfDayVn, expiryDateError, fieldForApiError, newIdempotencyKey, planRequiresExpiry } from '../plan-expiry'
import { INVOICE_STATUS, ORDER_STATUS, fmtDateTime, fmtDay, limitText, providerLabel } from './labels'

const PAGE_SIZE = 20

const QUOTA_SHORT: Record<string, string> = { members: 'nhân viên', officialChannels: 'kênh chính thức', personalChannels: 'tài khoản cá nhân' }
const deltaText = (d: object) =>
  Object.entries(d as Record<string, number>).filter(([, v]) => v > 0).map(([k, v]) => `+${v} ${QUOTA_SHORT[k] ?? k}`).join(', ')

const MODE_LABEL: Record<string, string> = {
  new: 'Mua mới (kỳ tính từ lúc thanh toán)',
  extend: 'Gia hạn nối tiếp từ ngày hết hạn',
  requantity: 'Đổi số lượng cùng gói — quy đổi kỳ cũ, kỳ mới từ lúc thanh toán',
  change_plan: 'Đổi gói — quy đổi gói cũ, kỳ mới từ lúc thanh toán',
}

export function BillingTab({ orgId }: { orgId: string }) {
  const { data, isLoading, isError } = useOrgBilling(orgId)
  const [dialog, setDialog] = useState<'plan' | 'extend' | 'expiry' | 'contract' | null>(null)
  if (isLoading) return <Loading label="Đang tải..." />
  if (isError || !data) return <ErrorState message="Không tải được thông tin gói." />
  const s = data.summary

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2">
            <CardTitle>Gói hiện tại</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setDialog('plan')}><Repeat className="h-4 w-4" /> Đổi gói</Button>
              <Button size="sm" variant="outline" onClick={() => setDialog('extend')}><CalendarPlus className="h-4 w-4" /> Gia hạn thủ công</Button>
              <Button size="sm" variant="outline" onClick={() => setDialog('expiry')}><CalendarClock className="h-4 w-4" /> Đặt hạn</Button>
              {data.isEnterprise && (
                <Button size="sm" onClick={() => setDialog('contract')}><FileSignature className="h-4 w-4" /> Hợp đồng Doanh nghiệp</Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[150px_1fr] gap-x-3 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Nhãn gói</dt><dd>{s?.plan ?? '(trống → Miễn phí)'}</dd>
              <dt className="text-muted-foreground">Gói hiệu lực</dt>
              <dd className="flex flex-wrap items-center gap-1.5">
                {s?.planName ?? 'Thoả thuận riêng'}
                {s?.wasDowngraded && <Badge variant="warning">Đã hạ về Miễn phí do hết hạn</Badge>}
              </dd>
              <dt className="text-muted-foreground">Trạng thái</dt><dd>{s && <OrgStatusBadge status={s.status} />}</dd>
              <dt className="text-muted-foreground">Hết hạn</dt><dd>{s?.expiresAt ? fmtDateTime(s.expiresAt) : 'Không giới hạn'}</dd>
              <dt className="text-muted-foreground">Kỳ hiện hành</dt>
              <dd>
                {data.currentOrder
                  ? `${fmtDay(data.currentOrder.periodStart)} → ${fmtDay(data.currentOrder.periodEnd)} · ${providerLabel(data.currentOrder.provider)}`
                  : <span className="text-muted-foreground">Không có đơn còn hiệu lực của gói này (gói đặt tay / miễn phí)</span>}
              </dd>
              <dt className="text-muted-foreground">Số lượng hiệu lực</dt>
              <dd>
                {s?.purchased
                  ? `${s.purchased.members} nhân viên · ${s.purchased.officialChannels} kênh chính thức · ${s.purchased.personalChannels} tài khoản cá nhân`
                    + (s.addonPurchased ? ` (gồm mua thêm ${deltaText(s.addonPurchased)})` : '')
                  : 'Theo số gồm sẵn của gói'}
              </dd>
              <dt className="text-muted-foreground">Hạn mức áp dụng</dt>
              <dd>
                {s && `${limitText(s.limits.members)} nhân viên · ${limitText(s.limits.officialChannels)} kênh chính thức · ${limitText(s.limits.personalChannels)} cá nhân · ${limitText(s.limits.activeContactsPerMonth)} khách/tháng · ${limitText(s.limits.aiRepliesPerDay)} AI/ngày`}
              </dd>
              {data.isEnterprise && (
                <>
                  <dt className="text-muted-foreground">Hợp đồng DN</dt>
                  <dd>
                    {data.contract
                      ? <Badge variant="success">Đang áp số theo hợp đồng</Badge>
                      : <span className="text-muted-foreground">Chưa đặt hợp đồng — không giới hạn</span>}
                  </dd>
                </>
              )}
            </dl>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Thống kê đơn</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Tổng đã thu</span><b className="tabular-nums">{formatVnd(data.orderStats.totalPaidAmount)}</b></div>
            {Object.entries(data.orderStats.byStatus).map(([k, n]) => (
              <div key={k} className="flex justify-between">
                <span className="text-muted-foreground">{ORDER_STATUS[k]?.text ?? k}</span>
                <span className="tabular-nums">{formatNumber(n)}</span>
              </div>
            ))}
            {Object.keys(data.orderStats.byStatus).length === 0 && <p className="text-muted-foreground">Chưa có đơn nào.</p>}
          </CardContent>
        </Card>
      </div>

      <OrdersSection orgId={orgId} />

      <ChangePlanDialog orgId={orgId} open={dialog === 'plan'} onOpenChange={(v) => setDialog(v ? 'plan' : null)} plans={data.plans} current={s?.plan ?? null}
        currentPlanId={s?.wasDowngraded ? 'free' : s?.effectivePlan ?? null} upgradeOnly={!!s?.upgradeOnly} expiresAt={s?.expiresAt ?? null} />
      <ExtendDialog orgId={orgId} open={dialog === 'extend'} onOpenChange={(v) => setDialog(v ? 'extend' : null)} plans={data.plans} currentPlan={s?.effectivePlan ?? null} />
      <ExpiryDialog orgId={orgId} open={dialog === 'expiry'} onOpenChange={(v) => setDialog(v ? 'expiry' : null)} expiresAt={s?.expiresAt ?? null} />
      {data.isEnterprise && dialog === 'contract' && (
        <ContractDialog orgId={orgId} open onOpenChange={(v) => setDialog(v ? 'contract' : null)}
          expiresAt={s?.expiresAt ?? null} contract={data.contract} />
      )}
    </div>
  )
}

// ── Lịch sử đơn ─────────────────────────────────────────────────────
function OrdersSection({ orgId }: { orgId: string }) {
  const [status, setStatus] = useState('all')
  const [page, setPage] = useState(1)
  const [openId, setOpenId] = useState<string | null>(null)
  const { data, isLoading, isError } = useOrgOrders(orgId, { status: status === 'all' ? undefined : status, page, limit: PAGE_SIZE })

  const columns: Column<OrgOrderRow>[] = [
    { key: 'created', header: 'Ngày tạo', cell: (o) => fmtDateTime(o.createdAt) },
    {
      key: 'plan', header: 'Gói', cell: (o) => (
        <div>
          <div>
            {o.kind === 'addon' && <Badge variant="secondary" className="mr-1">Mua thêm</Badge>}
            {o.kind !== 'addon' && o.mode === 'requantity' && <Badge variant="outline" className="mr-1">Đổi số lượng</Badge>}
            {o.planName}{o.days ? ` · ${o.days} ngày${o.kind === 'addon' ? ' còn lại' : ''}` : ''}
          </div>
          {o.manualReason && <div className="max-w-[220px] truncate text-xs text-muted-foreground" title={o.manualReason}>Lý do: {o.manualReason}</div>}
        </div>
      ),
    },
    { key: 'amount', header: 'Số tiền', align: 'right', cell: (o) => formatVnd(o.amount) },
    { key: 'credit', header: 'Quy đổi', align: 'right', cell: (o) => (o.creditUsed ? formatVnd(o.creditUsed) : '—') },
    { key: 'method', header: 'Phương thức', cell: (o) => providerLabel(o.provider) },
    {
      key: 'status', header: 'Trạng thái', cell: (o) => {
        const st = ORDER_STATUS[o.status] ?? { text: o.status, variant: 'secondary' as const }
        return <Badge variant={st.variant}>{st.text}</Badge>
      },
    },
    { key: 'paid', header: 'Thanh toán lúc', cell: (o) => fmtDateTime(o.paidAt) },
    { key: 'period', header: 'Kỳ', cell: (o) => (o.periodEnd ? `${fmtDay(o.periodStart)} → ${fmtDay(o.periodEnd)}` : '—') },
    { key: 'invoice', header: 'Hoá đơn', cell: (o) => (o.invoiceNo ? `Số ${o.invoiceNo}` : INVOICE_STATUS[o.invoiceStatus] ?? o.invoiceStatus) },
  ]

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle>Lịch sử đơn</CardTitle>
        <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1) }}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Mọi trạng thái</SelectItem>
            {Object.entries(ORDER_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.text}</SelectItem>)}
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent className="space-y-2">
        {isError ? <ErrorState message="Không tải được lịch sử đơn." /> : (
          <DataTable columns={columns} rows={data?.items ?? []} loading={isLoading} rowKey={(o) => o.id} onRowClick={(o) => setOpenId(o.id)} emptyTitle="Chưa có đơn" />
        )}
        {data && data.total > PAGE_SIZE && <Pagination page={page} limit={PAGE_SIZE} total={data.total} onPageChange={setPage} />}
      </CardContent>
      <OrderDetailDialog orgId={orgId} orderId={openId} onClose={() => setOpenId(null)} />
    </Card>
  )
}

function OrderDetailDialog({ orgId, orderId, onClose }: { orgId: string; orderId: string | null; onClose: () => void }) {
  const { data: o, isLoading } = useOrgOrder(orgId, orderId)
  const q = o?.quote
  const vb = o ? vatBreakdown(q, o.amount) : null
  const row = (label: string, value: React.ReactNode) => (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-all">{value ?? '—'}</dd>
    </>
  )
  return (
    <Dialog open={!!orderId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Chi tiết đơn</DialogTitle>
          <DialogDescription className="font-mono text-xs">{orderId}</DialogDescription>
        </DialogHeader>
        {isLoading || !o ? <Loading /> : (
          <div className="space-y-5 text-sm">
            <dl className="grid grid-cols-[150px_1fr] gap-x-3 gap-y-1.5">
              {row('Trạng thái', <Badge variant={ORDER_STATUS[o.status]?.variant ?? 'secondary'}>{ORDER_STATUS[o.status]?.text ?? o.status}</Badge>)}
              {row('Gói', `${q?.planName ?? o.planId} (${o.cycle === 'addon' ? 'mua thêm cho kỳ hiện tại' : o.cycle})`)}
              {q?.addon && row('Mua thêm', `${deltaText(q.addon.delta)} · ${q.addon.remainingDays} ngày còn lại, đến ${fmtDay(q.addon.periodEnd)}`)}
              {q?.mode && q.mode !== 'addon' && row('Cách áp', MODE_LABEL[q.mode] ?? q.mode)}
              {row('Số tiền', `${formatVnd(o.amount)}${vb && vb.amount > 0 ? ` (gồm ${vatLabel(vb.percent)})` : ''}`)}
              {row('Phương thức', providerLabel(o.provider))}
              {row('Người tạo', o.createdBy ? `${o.createdBy.name}${o.createdBy.email ? ` · ${o.createdBy.email}` : ''}${o.createdBy.kind === 'platform' ? ' (Platform)' : ''}` : '—')}
              {row('Tạo lúc', fmtDateTime(o.createdAt))}
              {row('Thanh toán lúc', fmtDateTime(o.paidAt))}
              {row('Kỳ dịch vụ', o.periodEnd ? `${fmtDateTime(o.periodStart)} → ${fmtDateTime(o.periodEnd)}` : '—')}
              {row('Mã giao dịch', o.zpTransId ?? o.paymentCode ?? o.appTransId ?? '—')}
              {o.providerTxnId && row('Mã phía cổng', o.providerTxnId)}
              {q?.manual?.reason && row('Lý do cấp tay', q.manual.reason)}
            </dl>

            {q && (
              <div className="space-y-2">
                <h4 className="font-medium">Báo giá lưu lúc đặt</h4>
                <div className="rounded-lg border">
                  <table className="w-full text-sm">
                    <tbody>
                      <tr className="border-b"><td className="px-3 py-1.5">Giá gốc / tháng</td><td className="px-3 py-1.5 text-right tabular-nums">{formatVnd(q.basePerMonth)}</td></tr>
                      {q.lines.map((l) => (
                        <tr key={l.key} className="border-b">
                          <td className="px-3 py-1.5">{l.label}: {l.quantity} (gồm {l.included}{l.extra > 0 ? `, thêm ${l.extra}` : ''})</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{l.monthly ? `+${formatVnd(l.monthly)}/tháng` : '—'}</td>
                        </tr>
                      ))}
                      <tr className="border-b"><td className="px-3 py-1.5">Số ngày</td><td className="px-3 py-1.5 text-right">{q.days}{q.extraDays ? ` (+${q.extraDays} ngày quy đổi)` : ''}</td></tr>
                      <tr className="border-b"><td className="px-3 py-1.5">Tạm tính</td><td className="px-3 py-1.5 text-right tabular-nums">{formatVnd(q.subtotal)}</td></tr>
                      {q.discount > 0 && <tr className="border-b"><td className="px-3 py-1.5">Giảm giá thời hạn ({q.discountPercent}%)</td><td className="px-3 py-1.5 text-right tabular-nums">−{formatVnd(q.discount)}</td></tr>}
                      {q.credit && <tr className="border-b"><td className="px-3 py-1.5">Quy đổi gói {q.credit.fromPlanName}</td><td className="px-3 py-1.5 text-right tabular-nums">−{formatVnd(q.credit.applied)}</td></tr>}
                      {vb && (
                        <>
                          <tr className="border-b font-medium"><td className="px-3 py-1.5">Tạm tính (chưa VAT)</td><td className="px-3 py-1.5 text-right tabular-nums">{formatVnd(vb.preTax)}</td></tr>
                          <tr className="border-b"><td className="px-3 py-1.5">{vatLabel(vb.percent)}</td><td className="px-3 py-1.5 text-right tabular-nums">{formatVnd(vb.amount)}</td></tr>
                        </>
                      )}
                      <tr className="font-medium"><td className="px-3 py-1.5">{vb ? 'Tổng thanh toán' : 'Phải trả'}</td><td className="px-3 py-1.5 text-right tabular-nums">{formatVnd(q.total)}</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <h4 className="font-medium">Hoá đơn điện tử</h4>
              <dl className="grid grid-cols-[150px_1fr] gap-x-3 gap-y-1.5">
                {row('Trạng thái', INVOICE_STATUS[o.invoiceStatus] ?? o.invoiceStatus)}
                {o.invoiceNo && row('Số hoá đơn', o.invoiceNo)}
                {o.invoiceIssuedAt && row('Xuất lúc', fmtDateTime(o.invoiceIssuedAt))}
                {o.invoiceError && row('Lỗi', <span className="text-destructive">{o.invoiceError}</span>)}
                {o.invoiceStatus !== 'none' && row('Số lần gửi', o.invoiceAttempts)}
                {o.invoiceInfo && row('Người mua', (() => {
                  const i = o.invoiceInfo as { companyName?: string | null; buyerName?: string | null; taxCode?: string | null }
                  return `${i.companyName ?? i.buyerName ?? '—'}${i.taxCode ? ` · MST ${i.taxCode}` : ''}`
                })())}
              </dl>
              {o.invoiceStatus !== 'none' && <p className="text-xs text-muted-foreground">Tải PDF/XML và thử xuất lại ở Platform → Hoá đơn.</p>}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ── Hộp thao tác ────────────────────────────────────────────────────
function planOptions(plans: CatalogPlan[], filter?: (p: CatalogPlan) => boolean) {
  return plans.filter((p) => (filter ? filter(p) : true)).map((p) => (
    <SelectItem key={p.id} value={p.id}>
      {p.name}{p.hidden ? ' (ẩn)' : ''}{p.monthlyPrice != null && p.monthlyPrice > 0 ? ` · ${formatVnd(p.monthlyPrice)}/tháng` : ''}
    </SelectItem>
  ))
}

function ChangePlanDialog({ orgId, open, onOpenChange, plans, current, currentPlanId, upgradeOnly, expiresAt }: {
  orgId: string; open: boolean; onOpenChange: (v: boolean) => void; plans: CatalogPlan[]; current: string | null
  /** Hạn hiện tại — điền sẵn ô ngày khi đổi sang gói trả phí (Q4). */
  expiresAt: string | null
  /** Gói hiệu lực (id bảng giá) + gói trả phí còn hạn — để cảnh báo khi quản trị hạ gói tay. */
  currentPlanId: string | null; upgradeOnly: boolean
}) {
  const m = useChangeOrgPlan(orgId)
  const [planId, setPlanId] = useState('')
  // Q4: gói trả phí bắt buộc ngày hết hạn — điền sẵn hạn hiện tại nếu còn trong tương lai.
  const [date, setDate] = useState(() => (expiresAt && new Date(expiresAt).getTime() > Date.now() ? toDateInput(expiresAt) : ''))
  const [dateError, setDateError] = useState<string | null>(null)
  const expiryRequired = planRequiresExpiry(plans.find((p) => p.id === planId))
  const dateInvalid = expiryDateError(date, expiryRequired)
  // Khách không tự hạ gói được khi còn hạn ("mua rồi không trả lại"); quản trị vẫn đổi tay được — chỉ cảnh báo.
  const lower = !!planId && !!currentPlanId && comparePlans(plans, planId, currentPlanId) === -1
  return (
    <ReasonDialog
      open={open} onOpenChange={onOpenChange} title="Đổi gói"
      description="Chỉ chọn gói trong bảng giá. Sang Miễn phí → vô thời hạn (xoá hạn); sang Doanh nghiệp → xoá hạn cũ, đặt hạn + số lượng ở “Hợp đồng Doanh nghiệp”; sang gói trả phí → bắt buộc nhập ngày hết hạn. Số lượng mua thêm chỉ tính theo đơn cùng gói nên đổi tay sẽ dùng số gồm sẵn của gói mới."
      confirmLabel="Đổi gói" pending={m.isPending} canConfirm={!!planId && planId !== current && !(expiryRequired && dateInvalid)}
      onConfirm={async (reason) => {
        try {
          const iso = expiryRequired ? endOfDayVn(date) : null
          await m.mutateAsync({ planId, reason, ...(iso ? { expiresAt: iso } : {}) })
          toast.success('Đã đổi gói')
          onOpenChange(false)
        } catch (err) {
          const message = platformApiError(err)
          if (fieldForApiError(apiErrorCode(err)) === 'expiresAt') setDateError(message)
          toast.error(message)
        }
      }}
    >
      <div className="space-y-1.5">
        <Label>Gói mới</Label>
        <Select value={planId} onValueChange={(v) => { setPlanId(v); setDateError(null) }}>
          <SelectTrigger><SelectValue placeholder="Chọn gói" /></SelectTrigger>
          <SelectContent>{planOptions(plans)}</SelectContent>
        </Select>
        {expiryRequired && (
          <div className="space-y-1.5 pt-2">
            <Label htmlFor="plan-expiry-date">Ngày hết hạn (bắt buộc với gói trả phí)</Label>
            <Input
              id="plan-expiry-date" type="date" value={date} required aria-invalid={!!(dateError ?? (date && dateInvalid))}
              onChange={(e) => { setDate(e.target.value); setDateError(null) }}
            />
            {(dateError ?? dateInvalid) && <p className="text-xs text-destructive">{dateError ?? dateInvalid}</p>}
          </div>
        )}
        {lower && (
          <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
            Đây là gói <b>thấp hơn</b> gói hiện tại.{upgradeOnly ? ' Gói hiện tại còn hạn — khách hàng không tự hạ gói được (không hoàn tiền phần đã mua).' : ''}{' '}
            Quản trị vẫn đổi được (ghi đè), hãy ghi rõ lý do; hệ thống không hoàn tiền tự động.
          </p>
        )}
      </div>
    </ReasonDialog>
  )
}

function ExtendDialog({ orgId, open, onOpenChange, plans, currentPlan }: {
  orgId: string; open: boolean; onOpenChange: (v: boolean) => void; plans: CatalogPlan[]; currentPlan: string | null
}) {
  const m = useExtendOrg(orgId)
  const [days, setDays] = useState('30')
  const [planId, setPlanId] = useState('current')
  const [qty, setQty] = useState<Partial<FormValues<keyof Quotas>>>({})
  const n = Number(days)
  const valid = Number.isInteger(n) && n >= 1 && n <= 1100
  const currentExtendable = plans.some((p) => p.id === currentPlan && !(p.monthlyPrice === 0 && !p.purchasable))
  const adj = quantityAdjust(qty)
  // Q6: một khoá idempotency cho mỗi lần mở hộp thoại — bấm trùng / gửi lại (kể cả sau lỗi mạng) không tạo đơn thứ hai.
  // Đóng hộp thoại → sinh khoá mới cho lần mở sau.
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey)
  const handleOpenChange = (v: boolean) => {
    if (!v) { setQty({}); setIdempotencyKey(newIdempotencyKey()) }
    onOpenChange(v)
  }
  return (
    <ReasonDialog
      open={open} onOpenChange={handleOpenChange} title="Gia hạn thủ công"
      description="Tạo đơn 0đ (Cấp tay) và áp như đơn đã thanh toán: cùng gói còn hạn → cộng nối tiếp từ hạn hiện tại; khác gói / đã hết hạn → tính từ bây giờ. Không điều chỉnh → chép y gói đang dùng (gồm phần mua thêm còn hiệu lực). Ngày tặng không được quy đổi thành tiền khi khách đổi gói."
      confirmLabel="Gia hạn" pending={m.isPending} canConfirm={valid && !adj.invalid && (planId !== 'current' || currentExtendable)}
      onConfirm={async (reason) => {
        try {
          const r = await m.mutateAsync({
            days: n, planId: planId === 'current' ? undefined : planId, reason, idempotencyKey,
            ...(adj.quantities ? { quantities: adj.quantities } : {}),
          })
          toast.success(`Đã gia hạn — hết hạn mới: ${r.expiresAt ? fmtDay(r.expiresAt) : 'không giới hạn'}`)
          handleOpenChange(false)
        } catch (err) { toast.error(platformApiError(err)) }
      }}
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="extend-days">Số ngày</Label>
          <Input id="extend-days" type="number" min={1} max={1100} value={days} onChange={(e) => setDays(e.target.value)} />
          <div className="flex gap-1">
            {[7, 30, 90, 365].map((d) => (
              <Button key={d} type="button" size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setDays(String(d))}>{d}</Button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>Gói</Label>
          <Select value={planId} onValueChange={setPlanId}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="current">Gói hiện tại{currentExtendable ? '' : ' (không gia hạn được)'}</SelectItem>
              {planOptions(plans, (p) => !(p.monthlyPrice === 0 && !p.purchasable))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Điều chỉnh số lượng (tuỳ chọn)</Label>
        <div className="grid grid-cols-3 gap-2">
          {QUOTA_FIELDS.map((f) => (
            <div key={f.key} className="space-y-1">
              <span className="text-xs text-muted-foreground">{f.label}</span>
              <Input type="number" min={0} inputMode="numeric" placeholder="Giữ nguyên" value={qty[f.key] ?? ''}
                onChange={(e) => setQty((q) => ({ ...q, [f.key]: e.target.value }))} />
            </div>
          ))}
        </div>
        {adj.invalid
          ? <p className="text-xs text-destructive">Số lượng phải là số nguyên không âm</p>
          : <p className="text-xs text-muted-foreground">Để trống = giữ như gói đang dùng. Không thấp hơn số gồm sẵn của gói.</p>}
      </div>
    </ReasonDialog>
  )
}

function ExpiryDialog({ orgId, open, onOpenChange, expiresAt }: {
  orgId: string; open: boolean; onOpenChange: (v: boolean) => void; expiresAt: string | null
}) {
  const m = useSetOrgExpiry(orgId)
  const [date, setDate] = useState(toDateInput(expiresAt))
  const [unlimited, setUnlimited] = useState(!expiresAt)
  return (
    <ReasonDialog
      open={open} onOpenChange={onOpenChange} title="Đặt hạn cấp phép"
      description="Ghi đè trực tiếp ngày hết hạn, không tạo đơn. Gói trả phí hết hạn → hạ về Miễn phí; gói khác hết hạn → khoá đăng nhập."
      confirmLabel="Lưu hạn" pending={m.isPending} canConfirm={unlimited || !!date}
      onConfirm={async (reason) => {
        try {
          // Hết hạn vào CUỐI ngày đã chọn (giờ Việt Nam).
          await m.mutateAsync({ expiresAt: unlimited ? null : new Date(`${date}T23:59:59+07:00`).toISOString(), reason })
          toast.success('Đã cập nhật hạn')
          onOpenChange(false)
        } catch (err) { toast.error(platformApiError(err)) }
      }}
    >
      <div className="space-y-2">
        <Label htmlFor="expiry-date">Ngày hết hạn</Label>
        <Input id="expiry-date" type="date" value={date} disabled={unlimited} onChange={(e) => setDate(e.target.value)} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)} />
          Không giới hạn (xoá hạn)
        </label>
      </div>
    </ReasonDialog>
  )
}

// ── Hợp đồng gói Doanh nghiệp (C2): hạn + số lượng riêng tổ chức ──────
function ContractDialog({ orgId, open, onOpenChange, expiresAt, contract }: {
  orgId: string; open: boolean; onOpenChange: (v: boolean) => void; expiresAt: string | null; contract: ContractLimits | null
}) {
  const m = useSaveOrgContract(orgId)
  const [date, setDate] = useState(toDateInput(expiresAt))
  const [unlimited, setUnlimited] = useState(!expiresAt)
  const [form, setForm] = useState(() => contractToForm(contract))
  const [clear, setClear] = useState(false)
  const limits = contractFromForm(form)
  return (
    <ReasonDialog
      open={open} onOpenChange={onOpenChange} title="Hợp đồng Doanh nghiệp"
      description="Hạn và số lượng theo hợp đồng riêng của tổ chức này. Hạn mức áp dụng = số theo hợp đồng; ô trống = không giới hạn. Hết hạn hợp đồng → khoá đăng nhập."
      confirmLabel="Lưu hợp đồng" pending={m.isPending} canConfirm={(unlimited || !!date) && (clear || !!limits)}
      onConfirm={async (reason) => {
        try {
          await m.mutateAsync({
            expiresAt: unlimited ? null : new Date(`${date}T23:59:59+07:00`).toISOString(),
            limits: clear ? null : limits, reason,
          })
          toast.success('Đã lưu hợp đồng Doanh nghiệp')
          onOpenChange(false)
        } catch (err) { toast.error(platformApiError(err)) }
      }}
    >
      <div className="space-y-2">
        <Label htmlFor="contract-date">Ngày hết hạn hợp đồng</Label>
        <Input id="contract-date" type="date" value={date} disabled={unlimited} onChange={(e) => setDate(e.target.value)} />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)} />
          Không thời hạn
        </label>
      </div>
      <div className="space-y-1.5">
        <Label>Số lượng trong gói</Label>
        <div className="grid grid-cols-2 gap-2">
          {CONTRACT_FIELDS.map((f) => (
            <div key={f.key} className="space-y-1">
              <span className="text-xs text-muted-foreground">{f.label}</span>
              <Input type="number" min={0} inputMode="numeric" placeholder="Không giới hạn" value={form[f.key]} disabled={clear}
                onChange={(e) => setForm((v) => ({ ...v, [f.key]: e.target.value }))} />
            </div>
          ))}
        </div>
        {!clear && !limits && <p className="text-xs text-destructive">Số lượng phải là số nguyên không âm</p>}
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={clear} onChange={(e) => setClear(e.target.checked)} />
          Xoá hợp đồng (về không giới hạn)
        </label>
      </div>
    </ReasonDialog>
  )
}
